import React, { PropsWithChildren, useEffect, useRef } from 'react';
import * as S from './PageTopControls.styles';
import { PageTopControlsProps } from './PageTopControls.types';
import { useScrollVisibility } from '@/hooks/useScrollVisibility';
import { usePageTopControlsStore } from '@/store/usePageTopControlsStore';
import { useBridgeState } from '@/bridge/hooks';
import { TOP_BANNER_HEIGHT } from '@/components/Banners/Banners.constants';
import { ANDROID_BANNER_HEIGHT } from '@/components/AndroidAppBanner/AndroidAppBanner.constants';
import { useIsTopBannerVisible } from '@/hooks/useIsTopBannerVisible';
import { useSearchStore } from '@/store/useSearchStore';
import { useAndroidBannersStore } from '@/store/useBannerStore/useAndroidBannerStore';
import useScreenSize from '@/hooks/useScreenSize';

export const PageTopControls: React.FC<
  PropsWithChildren<PageTopControlsProps>
> = ({ children, onInitialRender }) => {
  const { isVisible, scrollPos, isTopOfPage } = useScrollVisibility();
  const controlsRef = useRef<HTMLDivElement>(null);
  const isTopBannerVisible = useIsTopBannerVisible();
  const isAndroidBannerVisible = useAndroidBannersStore(
    state => state.isAndroidBannerVisible
  );
  const isSuggestionsOpened = useSearchStore(
    state => state.isSuggestionsOpened
  );
  const [
    topControlsHeight,
    setTopControlsHeight,
    canBeFixed,
    topControlsPosition,
    setTopControlsPosition,
  ] = usePageTopControlsStore(state => [
    state.topControlsHeight,
    state.setTopControlsHeight,
    state.canBeFixed,
    state.topControlsPosition,
    state.setTopControlsPosition,
  ]);
  const { isWebView } = useBridgeState();
  const { isLarge } = useScreenSize();

  useEffect(() => {
    if (controlsRef.current) {
      setTopControlsHeight(controlsRef.current.offsetHeight);
      onInitialRender?.(controlsRef.current.offsetHeight);
    }
  }, [isAndroidBannerVisible, scrollPos]);

  useEffect(() => {
    if (isLarge) {
      setTopControlsPosition('sticky');
    } else if (!isWebView) {
      const topControlsHeightFinal = isTopBannerVisible
        ? topControlsHeight + TOP_BANNER_HEIGHT
        : topControlsHeight;

      const topControlsHeightWithAndroid = isAndroidBannerVisible
        ? topControlsHeightFinal + ANDROID_BANNER_HEIGHT
        : topControlsHeightFinal;

      if (scrollPos >= topControlsHeightWithAndroid && canBeFixed) {
        setTopControlsPosition('fixed');
      }

      if (isTopOfPage) {
        setTopControlsPosition('relative');
      }
    }
  }, [isLarge, isWebView, scrollPos, topControlsHeight, canBeFixed]);

  return (
    <>
      {topControlsPosition === 'fixed' && (
        <S.PageTopControlsGutter $topControlsHeight={topControlsHeight} />
      )}
      <S.PageTopControlsBox
        ref={controlsRef}
        $isVisible={isVisible || isSuggestionsOpened}
        $position={topControlsPosition}
        $topControlsHeight={topControlsHeight}
        data-lotriver-header
      >
        {children}
      </S.PageTopControlsBox>
    </>
  );
};
